import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSuperAdmin } from '@nbh/shared';
import { useAuth } from '../auth/AuthProvider';
import { AdminDashboard } from '../components/AdminDashboard';
import { AssetManager } from '../components/admin/AssetManager';
import { ChecklistTemplateManager } from '../components/admin/ChecklistTemplateManager';
import { LocationManager } from '../components/admin/LocationManager';
import { PpmScheduleManager } from '../components/admin/PpmScheduleManager';
import { UserManager } from '../components/admin/UserManager';
import { AnalyticsBaselinePanel } from '../components/admin/AnalyticsBaselinePanel';
import { SlaPolicyPanel } from '../components/admin/SlaPolicyPanel';

type AdminTab = 'analytics' | 'locations' | 'assets' | 'checklists' | 'ppm' | 'users' | 'sla';

// Checklists sit before Maintenance: a schedule cannot be created without one, so
// the order matches the order the work has to happen in.
const TABS: AdminTab[] = ['analytics', 'locations', 'assets', 'checklists', 'ppm', 'users', 'sla'];

/**
 * The three root-tier surfaces: the property's topology, its equipment register and its
 * staff accounts.
 *
 * **Staff has been on and off this list twice, so the current reason is worth stating.**
 * D2 of the credentials work took it off and split the surface instead — an ADMIN kept
 * the roster and the forgotten-password reset, on the argument that a password forgotten
 * at the start of a night shift is the most common administrative act here and routing it
 * through an account that may be asleep and off-site turns a two-minute fix into a lost
 * shift. D44 puts it back and accepts that cost: the roster is names, addresses, roles and
 * last-seen times for every account including root, and the answer to the night shift is
 * two or three super-admins on the property rather than a widened guard.
 *
 * The cost is real and there is no fallback — self-service recovery went out with email
 * transport, so a forgotten password waits for whoever holds root.
 *
 * SLA joined at Q-CONFIG-NOW: the windows are the property's promise to its guests,
 * and rewriting a promise is root's to do. The endpoint refuses anyone else anyway.
 */
const ROOT_ONLY_TABS: readonly AdminTab[] = ['locations', 'assets', 'users', 'sla'];

/**
 * The tabs this role may see.
 *
 * Filtering the existing config array rather than introducing a route table: `TABS` is
 * already the nav config, and a second navigation pattern competing with the one in
 * `AppShell` would be worse than extending this one.
 *
 * Convenience, not the control. Every endpoint behind the three hidden tabs answers 403
 * to a non-root caller on its own — `super-admin.access.test.ts` asserts that for every
 * role — so this decides what somebody is *offered*, never what they can reach.
 *
 * Exported for its test.
 */
export function visibleAdminTabs(role: string | undefined): AdminTab[] {
  if (isSuperAdmin(role)) return TABS;

  return TABS.filter((name) => !ROOT_ONLY_TABS.includes(name));
}

/**
 * The management portal (SPEC.md §5.2, §5.4, §5.6).
 *
 * Route access is already restricted to ADMIN by the router, and every endpoint
 * behind these tabs re-checks the role server-side — the tab bar is convenience,
 * never the control.
 */
export function AdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState<AdminTab>('analytics');

  const tabs = visibleAdminTabs(user?.role);

  /**
   * The selected tab, or analytics if that selection is no longer on offer.
   *
   * Derived once rather than guarded per panel below. A demotion mid-session shrinks
   * `tabs` while `tab` still holds whichever the person last opened, and six separate
   * `&& maySee(...)` conditions would be six chances to forget one — including on the
   * next tab somebody adds. Analytics is the safe landing because it is never hidden.
   */
  const active: AdminTab = tabs.includes(tab) ? tab : 'analytics';

  return (
    <div>
      <nav
        className="flex overflow-x-auto border-b border-slate-800"
        aria-label={t('adminTabs.label')}
      >
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={active === name}
            onClick={() => setTab(name)}
            className={`shrink-0 border-b-2 bg-transparent px-4 py-3 text-sm font-medium ${
              active === name
                ? 'border-brookfields-gold text-slate-100'
                : 'border-transparent text-slate-400'
            }`}
          >
            {t(`adminTabs.${name}`)}
          </button>
        ))}
      </nav>

      <div className="p-4">
        {active === 'analytics' && <AdminDashboard windowDays={30} />}
        {active === 'locations' && <LocationManager />}
        {active === 'assets' && <AssetManager />}
        {active === 'checklists' && <ChecklistTemplateManager />}
        {active === 'ppm' && <PpmScheduleManager />}
        {active === 'users' && <UserManager />}
        {active === 'sla' && (
          <div className="space-y-10">
            <SlaPolicyPanel />
            <AnalyticsBaselinePanel />
          </div>
        )}
      </div>
    </div>
  );
}
